// src/pages/NewsPage.js
import React, { useEffect, useState } from "react";
import "./NewsPage.css";

import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsCardBig from "../components/NewsCardBig"; // Import the unified component

import { API_BASE } from "../config";

/* ---------------- FilterButtons ------------------------ */
const FilterButtons = ({ active, onChange, categories }) => (
  <div className="filter-buttons">
    {["All", ...categories].map((cat) => (
      <button
        key={cat}
        className={`filter-btn ${active === cat ? "active" : ""}`}
        onClick={() => onChange(cat)}
        aria-pressed={active === cat}
        type="button"
      >
        {cat}
      </button>
    ))}
  </div>
);

/* ---------------- LoadingSpinner ----------------------- */
const LoadingSpinner = () => (
  <div className="loading-spinner" role="status" aria-label="Loading news">
    <p>Loading news articles...</p>
  </div>
);

/* ---------------- ErrorMessage ------------------------- */
const ErrorMessage = ({ error }) => (
  <div className="error-message" role="alert">
    <p style={{ color: "red" }}>
      Failed to load news articles: {error}
    </p>
  </div>
);

/* ---------------- EmptyState --------------------------- */
const EmptyState = ({ category }) => (
  <div className="empty-state">
    <p>
      {category === "All"
        ? "No news articles available at the moment."
        : `No articles found in the "${category}" category.`
      }
    </p>
  </div>
);

/* ---------------- NewsPage root ------------------------ */
const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("All");

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/news`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setNews(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // derive category list from data
  const categories = [...new Set(news.map((n) => n.category))].filter(Boolean);

  const filtered =
    activeCat === "All"
      ? news
      : news.filter((n) => n.category === activeCat);

  return (
    <div className="page-wrapper news-page-wrapper">
      <Header />

      <div className="page-content">
        <main className="page-container">
          <h1 className="news-title">News</h1>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage error={error} />
          ) : news.length === 0 ? (
            <EmptyState category={activeCat} />
          ) : (
            <>
              <FilterButtons
                active={activeCat}
                onChange={setActiveCat}
                categories={categories}
              />
              <div className="news-list">
                {filtered.map((article) => (
                  <NewsCardBig key={article.news_id} item={article} />
                ))}
              </div>
            </>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default NewsPage;
